// "Remix in: Ableton · Audiotool · Browser" — the three ways out of a seed.
//
// One component for every place that offers them: the seed page's hero, its
// phone layout, the card below its fold, and each feed card. They drifted
// apart when they were four copies — the desktop hero lost the Browser button
// for a release, and nobody noticed because the other three still had it.
//
// Browser is a link: a producer who middle-clicks it gets the editor in a new
// tab, the way they expect of anything that opens somewhere else. Ableton and
// Audiotool are buttons, because neither is one destination any more — each
// opens a sheet that asks what you already have installed and routes from
// there (send straight to a running Live, or the download page).

import { useState } from "react";
import { Link } from "react-router";
import { localRoomPath } from "@/lib/ctas";
import { cn } from "@web-shared/lib/utils";
import { AbletonInterstitial } from "./AbletonInterstitial";
import { AudiotoolInterstitial } from "./AudiotoolInterstitial";

/**
 * `hero` is the tall pill row on the seed page, `compact` the equal-width row
 * in a card, `chip` the small row along a feed card's footer.
 */
export type RemixCtaSize = "hero" | "compact" | "chip";

const SHAPE: Record<RemixCtaSize, string> = {
  hero: "h-12 px-6 rounded-xl font-bold text-base",
  compact: "flex-1 py-2.5 rounded-lg font-bold text-xs sm:text-sm",
  chip: "h-7 px-2.5 rounded-md font-bold text-[11px]",
};

export function RemixCtas({
  seedId,
  size = "compact",
  className,
  seedTitle,
  producer,
  trackCount,
}: {
  seedId: string;
  size?: RemixCtaSize;
  className?: string;
  /** Seed context for the Ableton and Audiotool sheets. Pass it from every call site: without it they can't name the seed. */
  seedTitle?: string;
  producer?: string;
  trackCount?: number;
}) {
  const [showAbleton, setShowAbleton] = useState(false);
  const [showAudiotool, setShowAudiotool] = useState(false);
  const shape = cn("flex items-center justify-center whitespace-nowrap transition-all cursor-pointer", SHAPE[size]);
  const outlined = size === "hero" || size === "chip";

  return (
    <div className={cn("flex items-center", size === "chip" ? "gap-1.5" : "gap-2 sm:gap-3", className)}>
      <button
        type="button"
        onClick={() => setShowAbleton(true)}
        className={cn(shape, "bg-primary text-primary-foreground hover:bg-white", size !== "chip" && "shadow-glow")}
      >
        Ableton
      </button>
      {showAbleton && (
        <AbletonInterstitial
          seedId={seedId}
          seedTitle={seedTitle ?? "this seed"}
          producer={producer ?? ""}
          trackCount={trackCount ?? 0}
          onClose={() => setShowAbleton(false)}
        />
      )}
      <button
        type="button"
        onClick={() => setShowAudiotool(true)}
        className={cn(
          shape,
          outlined
            ? "border border-[#8b7cf6]/50 font-semibold text-[#c4b8ff] hover:bg-[#8b7cf6]/10"
            : "bg-[#7c5cbf] text-white hover:bg-[#9171d4]",
        )}
      >
        Audiotool
      </button>
      {showAudiotool && (
        <AudiotoolInterstitial
          seedId={seedId}
          seedTitle={seedTitle}
          producer={producer}
          trackCount={trackCount}
          onClose={() => setShowAudiotool(false)}
        />
      )}
      <Link
        to={localRoomPath(seedId)}
        className={cn(
          shape,
          outlined
            ? "border border-sky-400/50 font-semibold text-sky-200 hover:bg-sky-500/10"
            : "bg-sky-600 text-white hover:bg-sky-500",
        )}
      >
        Browser
      </Link>
    </div>
  );
}

/** The label above a row of them. */
export function RemixCtaLabel({ children = "Remix in" }: { children?: React.ReactNode }) {
  return <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{children}</div>;
}
